import gsap from 'gsap'
import 'lenis/dist/lenis.css'
import 'photoswipe/style.css'
import Swiper from 'swiper'
import 'swiper/css'
import 'swiper/css/effect-creative'
import { Autoplay, EffectCreative, Navigation, Pagination } from 'swiper/modules'

import '../css/main.css'
import { initLightbox } from './utils/lightbox'
import { createLineReveal } from './utils/lineReveal'
import { initRecaptchaForms } from './utils/recaptcha'
import { initRegistrationModal } from './utils/registrationModal'
import { createScrollFade } from './utils/scrollFade'
import { lockScroll, unlockScroll } from './utils/scrollLock'
import { refreshScroll } from './utils/scrollRefresh'
import { createScrollReveal } from './utils/scrollReveal'
import { initSmoothScroll } from './utils/smoothScroll'
import { initVideoModal } from './utils/videoModal'

const lenis = initSmoothScroll()
initMenu(lenis)
initHero()
initProjects()
initNews()
initCollection(lenis)
initNewsEvents(lenis)
initGallery()
initMilestones()
initReveals()
initFades()
initLightbox(lenis)
initVideoModal(lenis)
initRegistrationModal(lenis)
initRecaptchaForms()

/**
 * Mobile menu
 * disclosure: the toggle owns
 * aria-expanded/aria-controls, Escape closes, and focus moves to the first link
 * on open and back to the button on close. Scroll is locked with the shared
 * Lenis-aware helper, same as the lightbox and video modal.
 *
 * @param {import('lenis').default|null} [lenis] - smooth-scroll instance to pause
 */
function initMenu(lenis = null) {
  const panel = document.querySelector('.js-menu')
  const toggle = document.querySelector('.js-menu-toggle')
  if (!panel || !toggle) return

  const bar = document.querySelector('.js-header-bar')
  const iconOpen = toggle.querySelector('.js-menu-icon-open')
  const iconClose = toggle.querySelector('.js-menu-icon-close')
  const items = panel.querySelectorAll('.js-menu-item')
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  // Matches the panel's `lg:hidden` — see setOpen's resize guard below.
  const desktop = window.matchMedia('(min-width: 64rem)')

  let open = false

  const setOpen = (next) => {
    if (next === open) return
    open = next

    toggle.setAttribute('aria-expanded', String(open))
    toggle.setAttribute('aria-label', open ? 'Close menu' : 'Open menu')
    // toggleAttribute, not `.hidden =`: these are SVGElements, and the `hidden`
    // IDL property is only defined on HTMLElement — assigning it would set a
    // dead JS expando and leave the attribute (and so the icon) untouched.
    iconOpen?.toggleAttribute('hidden', open)
    iconClose?.toggleAttribute('hidden', !open)

    bar?.classList.toggle('bg-neutral-800/50', !open)
    bar?.classList.toggle('bg-night-800', open)

    if (open) {
      panel.hidden = false
      lockScroll(lenis)

      // Plain opacity, not autoAlpha: the `hidden` attribute already does the
      // showing/hiding, and autoAlpha's `visibility: hidden` at the start of the
      // tween would make the panel unfocusable just as we move focus into it.
      if (!reduceMotion) {
        gsap.fromTo(panel, { opacity: 0 }, { opacity: 1, duration: 0.3, ease: 'power2.out' })
        gsap.fromTo(
          items,
          { y: 24, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.5, ease: 'power3.out', stagger: 0.06, delay: 0.1 }
        )
      }

      panel.querySelector('a')?.focus()
      return
    }

    unlockScroll(lenis)
    // Only pull focus back when it's inside the panel we're closing — otherwise
    // a resize-driven close would steal it from wherever the user actually is.
    if (panel.contains(document.activeElement)) toggle.focus()

    if (reduceMotion) {
      panel.hidden = true
      return
    }
    gsap.to(panel, {
      opacity: 0,
      duration: 0.25,
      ease: 'power2.in',
      // A fast re-open can land before this fires; `open` tells us it did.
      onComplete: () => {
        if (!open) panel.hidden = true
      },
    })
  }

  toggle.addEventListener('click', () => setOpen(!open))

  // Links that navigate take the panel with them, but same-page ones (the `#`
  // Registration href) would otherwise leave it sitting open.
  panel.querySelectorAll('a').forEach((link) => link.addEventListener('click', () => setOpen(false)))

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') setOpen(false)
  })

  // The panel is `lg:hidden`, so widening past `lg` hides it in CSS while the
  // page underneath stays scroll-locked. Close it properly instead.
  desktop.addEventListener('change', (event) => {
    if (event.matches) setOpen(false)
  })
}

/**
 * Hero carousel: a custom "creative" transition on a looping autoplay timer
 * while a stories-style strip at the bottom — one segment per slide — tracks
 * progress: seen slides stay full, the active segment fills with the autoplay
 * countdown, and the strip empties again on the loop wrap. The current slide
 * stays static in the background; the incoming slide slides in over it from the
 * right (and slides back out to the right when stepping to the previous slide).
 * On every slide change GSAP settles the incoming image out of a slight zoom and
 * reveals the copy one line at a time, each sliding up from behind its own clip
 * mask, while the outgoing slide's image and copy drift and fade in a light
 * parallax (the copy leading the image) as the next slide sweeps over it.
 */
function initHero() {
  const root = document.querySelector('.js-hero')
  if (!root) return

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

  // One progress segment per slide (rendered in the markup, so it's free of the
  // loop's clones). The strip reads stories-style: segments before the active
  // slide are full, the active one fills with the autoplay countdown, the rest
  // are empty — so it doubles as a "which slide am I on" indicator.
  const segments = gsap.utils.toArray(root.parentElement.querySelectorAll('.js-hero-progress'))

  // Paint the strip for the active slide and how far its timer has run (0 → 1).
  const setProgress = (index, fill) => {
    segments.forEach((seg, i) => {
      let scaleX = 0
      if (i < index) scaleX = 1
      else if (i === index) scaleX = fill
      gsap.set(seg, { scaleX })
    })
  }

  // One line-reveal controller per slide (keyed by the slide element) so each
  // slide's copy can animate in on its own when the slide becomes active. The
  // reveal is delayed so it plays as the slide finishes sliding in, rather than
  // racing the incoming-slide motion (where it's over before you notice it).
  const reveals = new Map()

  // The CTA lands a beat after the copy reveal (which itself waits 0.7s); this
  // is the delay before its pill starts scaling open.
  const CTA_DELAY = 1.1

  // Animate the slide that just became active: the image settles out of a slight
  // zoom while the copy reveals line by line from behind its masks, then the CTA
  // gets its own distinct beat. Each entrance also clears any parallax/exit state
  // left over from when this slide last left view (slides are reused across the
  // loop), so it always starts from rest.
  const animateSlide = (slide) => {
    if (!slide) return

    const img = slide.querySelector('.js-hero-img')
    const copy = slide.querySelectorAll('.js-hero-anim')
    const cta = slide.querySelector('.js-hero-cta')
    const ctaLabel = cta?.querySelector('.js-hero-cta-label')

    if (!reduceMotion) {
      if (img) {
        // The slide itself slides in from the right (Swiper); keep the image
        // fully visible during that move and just let it settle out of a zoom.
        gsap.fromTo(
          img,
          { scale: 1.12, xPercent: 0 },
          { scale: 1, xPercent: 0, duration: 1.4, ease: 'power3.out', overwrite: 'auto' }
        )
      }
      // Cancel a still-running slide-out drift and snap the copy back to rest
      // before the line reveal plays (autoAlpha is restored by reveal.play()).
      if (copy.length) {
        gsap.killTweensOf(copy)
        gsap.set(copy, { yPercent: 0 })
      }
      // The CTA reads differently from the line reveal: the pill first scales
      // open from its left edge, then the label slides up from behind its tight
      // mask — a deliberate two-step "pop" that draws the eye to the action.
      if (cta) {
        const tl = gsap.timeline({ delay: CTA_DELAY })
        tl.fromTo(
          cta,
          { scale: 0, autoAlpha: 0 },
          {
            scale: 1,
            autoAlpha: 1,
            duration: 0.5,
            ease: 'back.out(1.5)',
            transformOrigin: 'center',
            overwrite: 'auto',
          }
        )
        if (ctaLabel) {
          tl.fromTo(
            ctaLabel,
            { yPercent: 110 },
            { yPercent: 0, duration: 0.45, ease: 'power3.out', overwrite: 'auto' },
            '>-0.1'
          )
        }
      }
    }

    reveals.get(slide)?.play()
  }

  // Parallax the outgoing slide as the next one slides over it: the image drifts
  // a little and zooms a touch (background) while the copy slides further and
  // fades out (foreground), so the hand-off reads with depth instead of the old
  // slide just sitting static. `goingBack` flips the drift direction so stepping
  // to the previous slide pushes the copy/image the other way.
  const slideOut = (slide, goingBack) => {
    if (!slide || reduceMotion) return

    const sign = goingBack ? 1 : -1
    const img = slide.querySelector('.js-hero-img')
    const copy = slide.querySelectorAll('.js-hero-anim')
    const cta = slide.querySelector('.js-hero-cta')

    if (img) {
      gsap.to(img, { xPercent: 15 * sign, scale: 1.06, duration: 0.9, ease: 'power2.out', overwrite: 'auto' })
    }
    if (copy.length) {
      gsap.to(copy, {
        yPercent: 40 * sign,
        autoAlpha: 0,
        duration: 0.9,
        ease: 'power2.out',
        overwrite: 'auto',
      })
    }
    // Collapse the pill back down as it leaves so it doesn't sit static under
    // the incoming slide (the entrance scales it open again from rest).
    if (cta) {
      gsap.to(cta, { scale: 0.85, autoAlpha: 0, duration: 0.45, ease: 'power2.in', overwrite: 'auto' })
    }
  }

  const swiper = new Swiper(root, {
    modules: [Autoplay, EffectCreative],
    effect: 'creative',
    // Current slide holds its place; the next slide translates in over it from
    // the right. Going back simply reverses: the active slide slides off to the
    // right to reveal the (static) previous one beneath it.
    creativeEffect: {
      perspective: false,
      prev: { translate: [0, 0, 0] },
      next: { translate: ['100%', 0, 0] },
    },
    speed: 900,
    loop: true,
    autoplay: {
      delay: 5500,
      disableOnInteraction: false,
    },
    on: {
      slideChangeTransitionStart(sw) {
        // Reset the strip to the new slide: earlier segments full, this one empty
        // and about to fill, later ones empty. The loop wrap (last → first) clears
        // the whole strip for a fresh pass through the set.
        setProgress(sw.realIndex, 0)

        const goingBack = sw.activeIndex < sw.previousIndex
        slideOut(sw.slides[sw.previousIndex], goingBack)
        animateSlide(sw.slides[sw.activeIndex])

        // The creative effect always stacks the incoming slide on top, which is
        // what we want going forwards (it slides in over the static one). Going
        // backwards the slide we're leaving is the one that has to move — so lift
        // it above the revealed slide, otherwise it slides off hidden behind it
        // and you only see the new slide pop in. Restored on transition end.
        if (goingBack) {
          const leaving = sw.slides[sw.previousIndex]
          if (leaving) {
            leaving.dataset.heroRestZ = leaving.style.zIndex
            leaving.style.zIndex = String(sw.slides.length + 10)
          }
        }
      },
      slideChangeTransitionEnd(sw) {
        sw.slides.forEach((slide) => {
          if (slide.dataset.heroRestZ !== undefined) {
            slide.style.zIndex = slide.dataset.heroRestZ
            delete slide.dataset.heroRestZ
          }
        })
      },
      // Fires roughly every frame; `percentage` runs 1 → 0 across the delay, so
      // `1 - percentage` is how far the active slide's segment has filled.
      autoplayTimeLeft(sw, _time, percentage) {
        setProgress(sw.realIndex, 1 - percentage)
      },
    },
  })

  // Build a reveal for every slide (loop clones are in the DOM by now; they
  // persist across resizes since the hero has no breakpoints), then play the
  // opening reveal for the starting slide.
  swiper.slides.forEach((slide) => {
    const copy = slide.querySelectorAll('.js-hero-anim')
    if (copy.length) reveals.set(slide, createLineReveal(copy, { delay: 0.7 }))

    // Pre-hide the CTA (the line reveal already hides .js-hero-anim) so no slide
    // shows its button before its own entrance scales it in.
    if (!reduceMotion) {
      const cta = slide.querySelector('.js-hero-cta')
      const ctaLabel = cta?.querySelector('.js-hero-cta-label')
      if (cta) gsap.set(cta, { scale: 0, autoAlpha: 0, transformOrigin: 'left center' })
      if (ctaLabel) gsap.set(ctaLabel, { yPercent: 110 })
    }
  })
  animateSlide(swiper.slides[swiper.activeIndex])

  root.parentElement.querySelector('.js-hero-prev')?.addEventListener('click', () => swiper.slidePrev())
  root.parentElement.querySelector('.js-hero-next')?.addEventListener('click', () => swiper.slideNext())

  // The bottom progress segments double as clickable pagination — jump to the
  // matching real slide (slideToLoop keeps the loop's index math correct).
  root.parentElement.querySelectorAll('.js-hero-progress-btn').forEach((btn, i) => {
    btn.addEventListener('click', () => swiper.slideToLoop(i))
  })
}

/**
 * Development highlights: a one-per-view slider driven by the custom prev/next
 * buttons and a fraction counter (1 / 3). GSAP lifts the title, image and copy
 * up on each slide change to echo the hero.
 */
function initProjects() {
  const root = document.querySelector('.js-projects')
  if (!root) return

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

  const animateSlide = (slide) => {
    if (!slide || reduceMotion) return

    const items = slide.querySelectorAll('.js-project-anim')
    if (!items.length) return

    gsap.fromTo(
      items,
      { y: 32, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.7, ease: 'power3.out', stagger: 0.1 }
    )
  }

  new Swiper(root, {
    modules: [Navigation, Pagination],
    speed: 600,
    navigation: {
      prevEl: '.js-projects-prev',
      nextEl: '.js-projects-next',
    },
    pagination: {
      el: '.js-projects-pagination',
      type: 'fraction',
    },
    on: {
      init(sw) {
        animateSlide(sw.slides[sw.activeIndex])
      },
      slideChangeTransitionStart(sw) {
        animateSlide(sw.slides[sw.activeIndex])
      },
    },
  })
}

/**
 * News & events: an auto-width slider whose cards bleed past the container on
 * the right (CSS handles the overflow). Driven by the custom prev/next buttons.
 */
function initNews() {
  const root = document.querySelector('.js-news')
  if (!root) return

  new Swiper(root, {
    modules: [Navigation],
    // 3 cards fill the container at desktop; fractional counts below let the
    // next card peek. Cards beyond the 3rd bleed past the container (overflow
    // visible) and are clipped at the viewport by the section's overflow-x-clip.
    slidesPerView: 1.15,
    spaceBetween: 16,
    speed: 500,
    navigation: {
      prevEl: '.js-news-prev',
      nextEl: '.js-news-next',
    },
    breakpoints: {
      640: { slidesPerView: 2, spaceBetween: 24 },
      1024: { slidesPerView: 3, spaceBetween: 28 },
    },
  })
}

/**
 * Single collection gallery: an auto-advancing-free slider whose images bleed
 * past the container on the right (CSS overflow, clipped at the viewport by the
 * section). Driven by the custom prev/next buttons with a fraction counter
 * (1 / 3) — the prev button starts in its disabled (greyed) state.
 */
function initGallery() {
  const root = document.querySelector('.js-gallery')
  if (!root) return

  new Swiper(root, {
    modules: [Navigation, Pagination],
    slidesPerView: 1.1,
    spaceBetween: 16,
    speed: 500,
    navigation: {
      prevEl: '.js-gallery-prev',
      nextEl: '.js-gallery-next',
    },
    pagination: {
      el: '.js-gallery-pagination',
      type: 'fraction',
    },
    breakpoints: {
      640: { slidesPerView: 1.3, spaceBetween: 24 },
      1024: { slidesPerView: 1.5, spaceBetween: 28 },
    },
  })
}

/**
 * About-page milestones: a timeline slider of year cards that bleed past the
 * container on the right (CSS overflow, clipped at the viewport by the section).
 * Driven by the custom prev/next buttons; the prev button starts disabled.
 */
function initMilestones() {
  const root = document.querySelector('.js-milestones')
  if (!root) return

  new Swiper(root, {
    modules: [Navigation],
    slidesPerView: 1.1,
    spaceBetween: 20,
    speed: 500,
    navigation: {
      prevEl: '.js-milestones-prev',
      nextEl: '.js-milestones-next',
    },
    breakpoints: {
      640: { slidesPerView: 2, spaceBetween: 24 },
      1024: { slidesPerView: 3, spaceBetween: 28 },
      1280: { slidesPerView: 3, spaceBetween: 68 },
    },
  })
}

/**
 * Collection grid: status filter tabs + "Load More". The cards are server-
 * rendered (driven by the `collections_list` ACF post type); this only toggles
 * which ones are visible. STEP cards show per "page"; the active tab filters by
 * the card's `data-status` ("all" shows everything). Newly revealed cards fade
 * up to echo the rest of the site's motion. Keep STEP in sync with $initial in
 * collection.php so the first render matches the server output.
 *
 * @param {import('lenis').default|null} [lenis] - smooth-scroll instance to re-measure
 */
function initCollection(lenis = null) {
  const root = document.querySelector('.js-collection')
  if (!root) return

  const STEP = 6
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

  const tabs = Array.from(root.querySelectorAll('.js-collection-tab'))
  const cards = Array.from(root.querySelectorAll('.js-collection-card'))
  const loadMore = root.querySelector('.js-collection-more')
  const empty = root.querySelector('.js-collection-empty')

  let filter = 'all'
  let limit = STEP

  const matches = (card) => filter === 'all' || card.dataset.status === filter

  const render = (animate = false) => {
    let shown = 0
    let total = 0
    const revealed = []

    cards.forEach((card) => {
      const match = matches(card)
      if (match) total += 1
      const visible = match && shown < limit
      if (visible) {
        if (card.hidden && animate) revealed.push(card)
        card.hidden = false
        shown += 1
      } else {
        card.hidden = true
      }
    })

    if (animate && revealed.length && !reduceMotion) {
      gsap.from(revealed, { y: 24, opacity: 0, duration: 0.6, ease: 'power3.out', stagger: 0.08 })
    }
    if (loadMore) loadMore.hidden = shown >= total
    if (empty) empty.hidden = total > 0
    // Filtering rewrites the page height, which leaves every trigger below the
    // grid measured against the old layout — re-measure so they still fire.
    if (animate) refreshScroll(lenis)
  }

  tabs.forEach((tab) => {
    tab.addEventListener('click', () => {
      if (tab.dataset.filter === filter) return
      filter = tab.dataset.filter
      limit = STEP

      tabs.forEach((t) => {
        const active = t === tab
        t.classList.toggle('text-brand', active)
        t.classList.toggle('border-brand', active)
        t.classList.toggle('text-white/60', !active)
        t.classList.toggle('border-transparent', !active)
        t.setAttribute('aria-selected', active ? 'true' : 'false')
      })

      render(true)
    })
  })

  loadMore?.addEventListener('click', () => {
    limit += STEP
    render(true)
  })

  render()
}

/**
 * News & Events page: a tab switch (News & Events / Videos), a year filter and
 * "Load More", all client-side over the server-rendered rows (see
 * news-events.php). Filtering by year resets the page; newly revealed rows fade
 * up. Keep STEP in sync with $initial in news-events.php.
 *
 * @param {import('lenis').default|null} [lenis] - smooth-scroll instance to re-measure
 */
function initNewsEvents(lenis = null) {
  const root = document.querySelector('.js-news-events')
  if (!root) return

  const STEP = 5
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

  const tabs = Array.from(root.querySelectorAll('.js-news-tab'))
  const panels = Array.from(root.querySelectorAll('.js-news-panel'))
  const rows = Array.from(root.querySelectorAll('.js-news-row'))
  const yearSelect = root.querySelector('.js-news-year')
  const loadMore = root.querySelector('.js-news-more')
  const empty = root.querySelector('.js-news-empty')

  let year = 'all'
  let limit = STEP

  const matches = (row) => year === 'all' || row.dataset.year === year

  // Show matching rows up to the current limit; toggle "Load More" / empty state.
  const render = (animate = false) => {
    let shown = 0
    let total = 0
    const revealed = []

    rows.forEach((row) => {
      const match = matches(row)
      if (match) total += 1
      const visible = match && shown < limit
      if (visible) {
        if (row.hidden && animate) revealed.push(row)
        row.hidden = false
        shown += 1
      } else {
        row.hidden = true
      }
    })

    if (animate && revealed.length && !reduceMotion) {
      gsap.from(revealed, { y: 24, opacity: 0, duration: 0.6, ease: 'power3.out', stagger: 0.08 })
    }
    if (loadMore) loadMore.hidden = shown >= total
    if (empty) empty.hidden = total > 0
    // Same as the collection grid: the row count just changed the page height.
    if (animate) refreshScroll(lenis)
  }

  tabs.forEach((tab) => {
    tab.addEventListener('click', () => {
      tabs.forEach((t) => {
        const active = t === tab
        t.classList.toggle('text-brand', active)
        t.classList.toggle('border-brand', active)
        t.classList.toggle('text-white/60', !active)
        t.classList.toggle('border-transparent', !active)
        t.setAttribute('aria-selected', active ? 'true' : 'false')
      })
      panels.forEach((panel) => {
        panel.hidden = panel.dataset.panel !== tab.dataset.tab
      })
      // Swapping panels swaps the page height too.
      refreshScroll(lenis)
    })
  })

  yearSelect?.addEventListener('change', () => {
    year = yearSelect.value
    limit = STEP
    render(true)
  })

  loadMore?.addEventListener('click', () => {
    limit += STEP
    render(true)
  })

  render()
}

/**
 * Section headings: reveal each one line by line the first time it scrolls into
 * view. Marked with `.js-reveal` in the markup; the hero and the in-slider
 * headings animate on their own (slide change) and are intentionally excluded.
 */
function initReveals() {
  createScrollReveal('.js-reveal')
}

/**
 * Everything else that isn't a heading or a slider: images, copy blocks, cards
 * and CTAs fade (and slide) up as they scroll into view. Items that cross the
 * trigger together stagger as a group.
 *
 * `.js-fade-up` is the default fade + slide-up. `.js-fade-in` is opacity-only —
 * used for Swiper slides (the news cards), where writing a transform would fight
 * Swiper's own slide positioning.
 */
function initFades() {
  createScrollFade('.js-fade-up')
  createScrollFade('.js-fade-in', { y: 0 })
}
